import { useEffect, useState } from "react";
import axios from "axios";
import useTranslator from "../../hooks/useTranslator";
import SkorLogo from "../../assets/img/skorLogo.svg";
import Flag from "../../assets/img/flag.svg";
import { getInitials } from "../../helper/applicationHelpers";
import "./BeneficiaryInsurancePayment.css";

const STATUS = {
  LOADING: "loading",
  REVIEW: "review",
  REDIRECTING: "redirecting",
  SUCCESS: "success",
  FAILURE: "failure",
  ERROR: "error",
};

const CenterLayout = ({ children }) => (
  <div className="bip-page">
    <div className="bip-card">{children}</div>
  </div>
);

const SpinnerIcon = () => (
  <div className="bip-spinner-wrap">
    <svg
      className="bip-spinner"
      width="42"
      height="42"
      viewBox="0 0 42 42"
      fill="none"
    >
      <circle cx="21" cy="21" r="17" stroke="#e8eceb" strokeWidth="4" />
      <path
        d="M38 21a17 17 0 0 0-17-17"
        stroke="#253837"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const StatusIcon = ({ type }) => {
  const isSuccess = type === "success";
  return (
    <div className={`bip-status-icon ${isSuccess ? "success" : "error"}`}>
      {isSuccess ? (
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
          <path
            d="M10.5 17.5l5 5 9-10"
            stroke="#253837"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
          <path
            d="M11 11l12 12M23 11L11 23"
            stroke="#c0392b"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      )}
    </div>
  );
};

const ActionButton = ({ children, onClick }) => (
  <button className="bip-btn" onClick={onClick}>
    {children}
  </button>
);

const BeneficiaryInsurancePayment = () => {
  const { _t, i18n } = useTranslator();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState("");
  const [details, setDetails] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem("i18nextLng")?.toLowerCase() || "es"
  );

  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, []);

  const handleLanguageChange = async (lang) => {
    await i18n.changeLanguage(lang);
    setCurrentLang(lang);
  };

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted && status === STATUS.REDIRECTING) {
        setPayLoading(false);
        setStatus(STATUS.ERROR);
        setErrorMessage(_t("invalidOrExpiredPaymentLink"));
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [status]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    const paymentStatus = params.get("status");

    window.history.replaceState({}, "", window.location.pathname);

    if (paymentStatus === "success") {
      setStatus(STATUS.SUCCESS);
      return;
    }

    if (paymentStatus === "failure") {
      setStatus(STATUS.FAILURE);
      return;
    }

    if (!urlToken) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("invalidOrExpiredPaymentLink"));
      return;
    }

    setToken(urlToken);
    fetchPaymentDetails(urlToken);
  }, []);

  const fetchPaymentDetails = async (token) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/insurance/beneficiary/payment-details`,
        {
          params: { token },
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      );

      setDetails(res.data?.success_res || null);
      setStatus(STATUS.REVIEW);
    } catch {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
    }
  };

  const handleContinueToPayment = () => {
    if (!confirmed || payLoading) return;
    setPayLoading(true);
    setStatus(STATUS.REDIRECTING);

    axios
      .post(
        `${process.env.REACT_APP_SERVER_URL}/insurance/beneficiary/initiate-payment`,
        { token },
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      )
      .then((res) => {
        const paymentUrl = res.data?.success_res?.paymentUrl;

        if (!paymentUrl) {
          setStatus(STATUS.ERROR);
          setErrorMessage(_t("paymentLinkNotReceived"));
          return;
        }

        window.location.href = paymentUrl;
      })
      .catch(() => {
        setStatus(STATUS.ERROR);
        setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
      })
      .finally(() => {
        setPayLoading(false);
      });
  };

  if (status === STATUS.LOADING || status === STATUS.REDIRECTING) {
    return (
      <CenterLayout>
        <SpinnerIcon />
        <h2 className="bip-title">
          {status === STATUS.REDIRECTING
            ? _t("redirectingToPayment")
            : _t("verifyingYourPaymentLink")}
        </h2>
        <p className="bip-text">
          {_t(
            "pleaseWaitWhileWeSecurelyProcessYourPayment",
            "Please wait while we securely process your payment."
          )}
        </p>
      </CenterLayout>
    );
  }

  if (status === STATUS.REVIEW) {
    const payerName =
      [details?.beneficiary?.first_name, details?.beneficiary?.last_name]
        .filter(Boolean)
        .join(" ") || "—";
    const amount =
      details?.insurance?.total_amount != null
        ? `€${details.insurance.total_amount}`
        : "—";
    const propertyAddress = details?.property?.address || "—";
    const monthlyRent =
      details?.insurance?.monthly_rent != null
        ? `€${details.insurance.monthly_rent}/${_t("perMonth")}`
        : "—";
    const leaseDuration = details?.insurance?.lease_duration
      ? `${details.insurance.lease_duration} ${_t("months")}`
      : "—";
    const startDate = details?.insurance?.start_date || "—";
    const policyStartDate = details?.insurance?.policy_start_date || "—";
    const tenants = (details?.selected_tenants || []).map((tenant) => ({
      name: [tenant.first_name, tenant.last_name].filter(Boolean).join(" "),
    }));
    const cdnBase = `${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}`;
    const rentalAgreementUrl = details?.rental_contract_url
      ? `${cdnBase}/${details.rental_contract_url}`
      : null;
    const insurancePolicyUrl = details?.prefilled_insurance_url
      ? `${cdnBase}/${details.prefilled_insurance_url}`
      : null;

    return (
      <div className="bip-review-page">
        <div className="bip-review-container">
          <div className="bip-review-header">
            <img className="bip-review-logo" src={SkorLogo} alt="Skor" />
            {currentLang === "es" ? (
              <div
                className="language-switch-container language-switch-container-es cursor-pointer"
                onClick={() => handleLanguageChange("en-us")}
              >
                <p>ES</p>
                <img
                  className="flag-image"
                  alt="Flag"
                  src="/images/spain-flag.png"
                />
              </div>
            ) : (
              <div
                className="language-switch-container cursor-pointer"
                onClick={() => handleLanguageChange("es")}
              >
                <img className="flag-image" alt="Flag" src={Flag} />
                <p>EN</p>
              </div>
            )}
          </div>

          <div className="bip-review-card">
            <p className="bip-review-title">{_t("reviewBeforePayment")}</p>
            <p className="bip-review-subtitle">
              {_t(
                "reviewBeforePaymentSubtitle",
                "Please confirm that all the information is correct before proceeding with the rent protection insurance payment."
              )}
            </p>
          </div>

          <div className="bip-review-grid">
            <div className="bip-review-card">
              <div className="bip-review-section-label">
                {_t("youAreThePolicyholder")}
              </div>
              <div className="bip-review-insured-card">
                <div>
                  <div className="bip-review-insured-label">
                    {_t("payingForThisInsurance")}
                  </div>
                  <div className="bip-review-insured-name">{payerName}</div>
                </div>
              </div>
            </div>

            <div className="bip-review-card">
              <div className="bip-review-section-label">
                {_t("amountToPay")}
              </div>
              <div className="bip-review-row bip-review-row--tight">
                <span className="bip-review-label">
                  {_t(
                    "annualRentProtectionInsurance",
                    "Annual Rent Protection Insurance"
                  )}
                </span>
                <span className="bip-review-amount">{amount}</span>
              </div>
            </div>
          </div>

          <div className="bip-review-grid">
            <div className="bip-review-card">
              <div className="bip-review-section-label">{_t("property")}</div>
              <div className="bip-review-row">
                <span className="bip-review-label">{_t("address")}</span>
                <span className="bip-review-value">{propertyAddress}</span>
              </div>

              <div className="bip-review-section-label bip-review-section-label--spaced">
                {_t("rentalTerms")}
              </div>
              <div className="bip-review-row">
                <span className="bip-review-label">
                  {_t("monthlyRent", "Monthly Rent")}
                </span>
                <span className="bip-review-value">{monthlyRent}</span>
              </div>
              <div className="bip-review-row">
                <span className="bip-review-label">
                  {_t("leaseDuration", "Lease Duration")}
                </span>
                <span className="bip-review-value">{leaseDuration}</span>
              </div>
              <div className="bip-review-row">
                <span className="bip-review-label">
                  {_t("startDate", "Start Date")}
                </span>
                <span className="bip-review-value">{startDate}</span>
              </div>
              <div className="bip-review-row">
                <span className="bip-review-label">
                  {_t("policyStartDate")}
                </span>
                <span className="bip-review-value">{policyStartDate}</span>
              </div>
            </div>

            <div className="bip-review-card">
              <div className="bip-review-section-label">{_t("tenants")}</div>
              {tenants.length ? (
                tenants.map((tenant, idx) => (
                  <div className="bip-review-tenant" key={idx}>
                    <div className="bip-review-avatar">
                      {getInitials(tenant.name)}
                    </div>
                    <div className="bip-review-tenant-name">{tenant.name}</div>
                  </div>
                ))
              ) : (
                <div className="bip-review-tenant-name">—</div>
              )}
            </div>
          </div>

          {(rentalAgreementUrl || insurancePolicyUrl) && (
            <div className="bip-review-card">
              <div className="bip-review-section-label">
                {_t("relevantDocuments", "Relevant Documents")}
              </div>
              <div className="bip-review-docs">
                {rentalAgreementUrl && (
                  <a
                    href={rentalAgreementUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="bip-review-doc-link"
                  >
                    <span className="bip-review-doc-icon">
                      <span className="bip-review-doc-badge">📄</span>
                      {_t("rentalAgreement", "Rental Agreement")}
                    </span>
                    <span className="bip-review-download-arrow">↓</span>
                  </a>
                )}
                {insurancePolicyUrl && (
                  <a
                    href={insurancePolicyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="bip-review-doc-link"
                  >
                    <span className="bip-review-doc-icon">
                      <span className="bip-review-doc-badge">🛡️</span>
                      {_t("insurancePolicy", "Insurance Policy")}
                    </span>
                    <span className="bip-review-download-arrow">↓</span>
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="bip-review-card">
            <div className="bip-review-confirm-row">
              <input
                type="checkbox"
                id="bip-confirm"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <label htmlFor="bip-confirm">
                {_t(
                  "confirmDetailsCheckboxLabel",
                  "I have reviewed the property, the tenants, and the rental terms, and I confirm that all the information is correct."
                )}
              </label>
            </div>
            <button
              className="bip-review-cta"
              disabled={!confirmed || payLoading}
              onClick={handleContinueToPayment}
            >
              {_t("continueToPayment", "Continue to Payment")}
            </button>
            <p className="bip-review-footer-note">
              {_t(
                "linkSingleUseExpiryNote",
                "This link can only be used once and expires in 24 hours."
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === STATUS.SUCCESS) {
    return (
      <CenterLayout>
        <StatusIcon type="success" />
        <h2 className="bip-title">{_t("paymentSuccessful")}</h2>
        <p className="bip-text">
          {_t(
            "yourPaymentHasBeenCompletedSuccessfully",
            "Your payment has been completed successfully."
          )}
        </p>
      </CenterLayout>
    );
  }

  if (status === STATUS.FAILURE) {
    return (
      <CenterLayout>
        <StatusIcon type="error" />
        <h2 className="bip-title">{_t("paymentNotCompleted")}</h2>
        <p className="bip-text">
          {_t(
            "yourPaymentCouldNotBeCompletedPleaseTryAgain",
            "Your payment could not be completed. Please try again."
          )}
        </p>
      </CenterLayout>
    );
  }

  // ERROR state
  return (
    <CenterLayout>
      <img className="bip-logo" src={SkorLogo} alt="Skor" />
      <h2 className="bip-title">{_t("unableToProcessPayment")}</h2>
      <p className="bip-text bip-error-text">{errorMessage}</p>
    </CenterLayout>
  );
};

export default BeneficiaryInsurancePayment;
